export const FRAME_AUTH_TOKEN = 'auth:token';
export const FRAME_AUTH_HELLO = 'auth:hello';
export const FRAME_AUTH_EXPIRED = 'auth:expired';
export const FRAME_AUTH_LOGOUT = 'auth:logout';
export const FRAME_AUTH_APP = 'uservideo';
export const ACCESS_COOKIE = 'APP_AUTH_ACCESS';
export const HELLO_TIMEOUT_MS = 3000;
export const TOKEN_WAIT_MS = 10000;

export const TRUSTED_ZONES = [
  'onclass.com',
  'onclass.me',
  'onclass.tech',
  'onclass.space',
  'onclass.local',
] as const;

export type FrameAuthTokenMessage = {
  type: typeof FRAME_AUTH_TOKEN;
  access?: unknown;
  expiresAt?: unknown;
  userId?: unknown;
  lang?: unknown;
};

export function isEmbeddedFrame(): boolean {
  try {
    return window.parent !== window;
  } catch {
    return true;
  }
}

export function originOf(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  try {
    return new URL(value).origin;
  } catch {
    try {
      return new URL(`https://${value}`).origin;
    } catch {
      return '';
    }
  }
}

export function isTrustedOnclassOrigin(origin: string | null | undefined): boolean {
  if (!origin) {
    return false;
  }
  try {
    const url = new URL(origin);
    if (!isAllowedProtocol(url)) {
      return false;
    }
    const host = url.hostname;
    return TRUSTED_ZONES.some((zone) => host === zone || host.endsWith(`.${zone}`));
  } catch {
    return false;
  }
}

function isAllowedProtocol(url: URL): boolean {
  if (url.protocol === 'https:') {
    return true;
  }
  return url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname.endsWith('.local'));
}

export function isVideoappHostname(hostname: string): boolean {
  return hostname === 'videoapp' || hostname.startsWith('videoapp.');
}

export function apiHostToPortalOrigin(host: string): string {
  try {
    const url = host.includes('://') ? new URL(host) : new URL(`https://${host}`);
    const hostname = url.hostname;
    if (isVideoappHostname(hostname)) {
      return '';
    }
    for (const zone of TRUSTED_ZONES) {
      if (hostname === zone || hostname.endsWith(`.${zone}`)) {
        return `${url.protocol}//${zone}`;
      }
    }
    return '';
  } catch {
    return '';
  }
}

export function resolveParentOrigin(): string {
  const fromReferrer = originOf(document.referrer);
  if (fromReferrer && isTrustedOnclassOrigin(fromReferrer)) {
    return fromReferrer;
  }

  const ancestor = window.location.ancestorOrigins?.[0];
  if (ancestor && isTrustedOnclassOrigin(ancestor)) {
    return ancestor;
  }

  const host = new URLSearchParams(window.location.search).get('host');
  if (host) {
    const portal = apiHostToPortalOrigin(host);
    if (portal && isTrustedOnclassOrigin(portal)) {
      return portal;
    }
  }

  return '';
}

export function stripBearer(token: string): string {
  return token.replace(/^bearer\s+/i, '').trim();
}

export function describeAccessToken(token: string | null | undefined): {
  present: boolean;
  parts: number;
  alg?: string;
  guest?: boolean;
  userId?: string;
  exp?: number;
  expired?: boolean;
} {
  const access = token ? stripBearer(token) : '';
  if (!access) {
    return { present: false, parts: 0 };
  }
  const parts = access.split('.');
  const payload = parts.length === 3 ? decodeJwtPayload(access) : null;
  let alg: string | undefined;
  if (parts.length === 3) {
    try {
      const header = JSON.parse(atob(parts[0].replace(/-/g, '+').replace(/_/g, '/')));
      alg = typeof header?.alg === 'string' ? header.alg : undefined;
    } catch {
      alg = undefined;
    }
  }
  const exp = typeof payload?.exp === 'number' ? payload.exp : undefined;
  return {
    present: true,
    parts: parts.length,
    alg,
    guest: payload?.guest === true,
    userId: payload?.subject?.id,
    expired: typeof exp === 'number' ? exp * 1000 <= Date.now() : undefined,
  };
}

function decodeJwtPayload(token: string): { subject?: { id?: string }; guest?: boolean; exp?: number } | null {
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }
  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

export function pickBestAccessToken(values: string[]): string | null {
  const cleaned = values.map((value) => stripBearer(value)).filter(Boolean);
  if (!cleaned.length) {
    return null;
  }

  const tokens = cleaned.filter((value) => value && value.split('.').length === 3);
  if (!tokens.length) {
    return cleaned[cleaned.length - 1] ?? cleaned[0] ?? null;
  }

  const scored = tokens
    .map((token) => {
      const payload = decodeJwtPayload(token);
      return {
        token,
        userId: payload?.subject?.id,
        guest: payload?.guest === true,
        exp: typeof payload?.exp === 'number' ? payload.exp : 0,
      };
    })
    .filter((item) => item.userId);

  const users = scored.filter((item) => !item.guest);
  const pool = users.length ? users : scored;
  pool.sort((a, b) => b.exp - a.exp);
  return pool[0]?.token || tokens[0] || cleaned[cleaned.length - 1] || null;
}

export function readAccessCookie(): string | null {
  const values: string[] = [];
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName !== ACCESS_COOKIE) {
      continue;
    }
    try {
      const value = decodeURIComponent(rest.join('=') || '');
      if (value) {
        values.push(value);
      }
    } catch {
      /* skip broken cookie */
    }
  }
  return pickBestAccessToken(values);
}

export function isFrameAuthToken(data: unknown): data is FrameAuthTokenMessage {
  return !!data && typeof data === 'object' && (data as { type?: unknown }).type === FRAME_AUTH_TOKEN;
}

export function isFrameAuthLogout(data: unknown): boolean {
  return !!data && typeof data === 'object' && (data as { type?: unknown }).type === FRAME_AUTH_LOGOUT;
}

export function isLegacyAuthString(data: unknown): boolean {
  return typeof data === 'string' && /^(gamelib|chatapp)::token::/.test(data);
}
